import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  Schedule,
  CreditCard,
  Subscriptions,
  Mail,
  VpnKey,
} from '@mui/icons-material';

const StyledCard = styled(Card)(() => ({
  borderRadius: '12px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  border: '1px solid #f1f5f9',
}));

const ActivityItem = ({ type, title, description, time, status, statusColor }) => {
  const getIcon = () => {
    switch (type) {
      case 'api_call':
        return <CheckCircle sx={{ fontSize: '2.25rem' }} />;
      case 'credit_purchase':
        return <CreditCard sx={{ fontSize: '2.25rem' }} />;
      case 'subscription':
        return <Subscriptions sx={{ fontSize: '2.25rem' }} />;
      case 'support_mail':
        return <Mail sx={{ fontSize: '2.25rem' }} />;
      case 'key_generation':
        return <VpnKey sx={{ fontSize: '2.25rem' }} />;
      default:
        return <Schedule sx={{ fontSize: '2.25rem' }} />;
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'api_call':
        return '#10b981';
      case 'credit_purchase':
        return '#f59e0b';
      case 'subscription':
        return '#6366f1';
      case 'support_mail':
        return '#3b82f6';
      case 'key_generation':
        return '#8b5cf6';
      default:
        return '#64748b';
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 2 }}>
      <Avatar
        sx={{
          bgcolor: `${getIconColor()}15`,
          color: getIconColor(),
          width: 32,
          height: 32,
        }}
      >
        {getIcon()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: '#1e293b',
                fontSize: '14px',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '12px',
                mb: 0.5,
              }}
            >
              {description}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#94a3b8',
                fontSize: '11px',
              }}
            >
              {time}
            </Typography>
          </Box>
          {status && (
            <Chip
              label={status}
              size="small"
              sx={{
                backgroundColor: `${statusColor}15`,
                color: statusColor,
                fontSize: '10px',
                fontWeight: 600,
                height: 20,
                ml: 1,
              }}
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

ActivityItem.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  time: PropTypes.string,
  status: PropTypes.string,
  statusColor: PropTypes.string,
};

export const RecentActivity = ({ onViewAll, activities = [] }) => {
  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              fontSize: '20px',
            }}
          >
            Recent Activity
          </Typography>
          <Button
            variant="text"
            onClick={onViewAll}
            sx={{
              color: '#6366f1',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#6366f115',
              },
            }}
          >
            View All Activity
          </Button>
        </Stack>
        
        {activities.length > 0 ? (
          activities.map((activity, index) => (
            <Box key={activity.id ?? `${activity.title}-${activity.time}`}>
              <ActivityItem {...activity} />
              {index < activities.length - 1 && (
                <Divider sx={{ my: 1, borderColor: '#f1f5f9' }} />
              )}
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}
          >
            No recent activity found
          </Typography>
        )}
      </CardContent>
    </StyledCard>
  );
};

RecentActivity.propTypes = {
  onViewAll: PropTypes.func,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      time: PropTypes.string,
      status: PropTypes.string,
      statusColor: PropTypes.string,
    })
  ),
};
